import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { TimeEntry } from "./use-time-registration";
import { osloDateString } from "@/lib/oslo-time";

export interface DayOverview {
  date: string; // YYYY-MM-DD
  dayOfWeek: string; // "mandag", "tirsdag", etc
  totalHours: number;
  entries: TimeEntry[];
}

/**
 * Fetch day-by-day time entries for a user in a given month.
 * Groups by date and returns with Norwegian day names.
 */
export function useMonthTimeOverview(
  userId: string | null,
  year: number,
  month: number
) {
  return useQuery({
    queryKey: ["month-time-overview", userId, year, month],
    enabled: !!userId,
    queryFn: async (): Promise<DayOverview[]> => {
      if (!userId) return [];

      // Build date range for the month.
      // A4-smoke-fiks: PostgREST tolker en dato-only-streng ("YYYY-MM-DD") mot
      // en `timestamptz`-kolonne som UTC-midnatt. En inklusiv
      // `.lte("start_date", <siste dag i måneden>)` ekskluderer derfor stille
      // enhver føring med et EKTE klokkeslett på månedens siste dag (Oslo er
      // UTC+1/+2, så ethvert klokkeslett etter midnatt UTC havner over grensen)
      // → siste dag forsvant fra "Avslutt måned"-oversikten. Vi bruker i stedet
      // en EKSKLUSIV øvre grense på første dag i NESTE måned. Samme rot som
      // fetchTimeEntries i use-time-registration.ts (bølge 2-smoke).
      const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const endDate = new Date(year, month, 0); // Last day of month
      const nextMonthYear = month === 12 ? year + 1 : year;
      const nextMonth = month === 12 ? 1 : month + 1;
      const exclusiveEnd = `${nextMonthYear}-${String(nextMonth).padStart(
        2,
        "0"
      )}-01`;

      const { data, error } = await supabase
        .from("time_entries")
        .select("*")
        .eq("user_id", userId)
        .gte("start_date", startDate)
        .lt("start_date", exclusiveEnd)
        .is("deleted_at", null)
        .order("start_date", { ascending: true });

      if (error) throw error;

      const entries = (data as TimeEntry[]) || [];

      // Group by date
      const dayMap = new Map<string, TimeEntry[]>();
      entries.forEach((entry) => {
        // Oslo-dato, ikke UTC: en føring kl. 00:30 hører til samme dag som i
        // useTimeGaps, så rad og hull-telling er enige (VIB-3034).
        const date = osloDateString(entry.start_date);
        if (!dayMap.has(date)) {
          dayMap.set(date, []);
        }
        dayMap.get(date)!.push(entry);
      });

      // Convert to DayOverview with Norwegian day names
      const dayNames = [
        "søndag",
        "mandag",
        "tirsdag",
        "onsdag",
        "torsdag",
        "fredag",
        "lørdag",
      ];

      const result: DayOverview[] = [];
      for (let day = 1; day <= endDate.getDate(); day++) {
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
        const dateObj = new Date(dateStr);
        const dayOfWeek = dayNames[dateObj.getDay()];

        const dayEntries = dayMap.get(dateStr) || [];
        const totalHours = dayEntries.reduce((sum, e) => {
          // Prefer manual override, fallback to calculated hours from seconds
          const hours =
            e.manual_hours_override ?? e.duration_seconds / 3600;
          return sum + hours;
        }, 0);

        result.push({
          date: dateStr,
          dayOfWeek,
          totalHours,
          entries: dayEntries,
        });
      }

      return result;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
